/**
 * Un encodeur GIF89a animé, en entier, sans dépendance.
 *
 * Le format demandé par la fabrique de bannières est celui de 1989 : palette
 * globale, boucle Netscape, compression LZW. Aucune bibliothèque n'est
 * installée pour ça — la plus légère pèse plus que tout le reste du skin, et le
 * site tient à n'avoir aucune dépendance de rendu.
 *
 * Le module ne touche ni au DOM ni au canvas : il reçoit des index de palette
 * et rend des octets. C'est ce qui le rend vérifiable — un décodeur écrit à
 * part, dans les tests, relit ce que celui-ci écrit, et le navigateur sert
 * d'arbitre final en affichant le fichier.
 */

/** Une image de l'animation. */
export interface GifFrame {
  /** Un index de palette par pixel, ligne par ligne, de haut en bas. */
  readonly indices: Uint8Array;
  /** Durée d'affichage. Le format compte en centièmes de seconde. */
  readonly delayMs: number;
}

export interface GifSpec {
  readonly width: number;
  readonly height: number;
  /** Couleurs `#rrggbb`, 256 au plus. */
  readonly palette: readonly string[];
  readonly frames: readonly GifFrame[];
}

/** Flux d'octets, en tableau simple : les tailles en jeu sont de l'ordre du Ko. */
class Bytes {
  private readonly out: number[] = [];

  byte(value: number): void {
    this.out.push(value & 0xff);
  }

  /** Entier 16 bits, petit-boutiste — l'ordre du format. */
  short(value: number): void {
    this.out.push(value & 0xff, (value >> 8) & 0xff);
  }

  ascii(value: string): void {
    for (const char of value) this.out.push(char.charCodeAt(0));
  }

  all(values: readonly number[]): void {
    for (const value of values) this.out.push(value & 0xff);
  }

  /*
   * Le tampon est annoncé sur `ArrayBuffer` et non sur `ArrayBufferLike` :
   * sans ça, `Blob` refuse le tableau, un `SharedArrayBuffer` n'étant pas
   * transférable.
   */
  done(): Uint8Array<ArrayBuffer> {
    return Uint8Array.from(this.out);
  }
}

/** `#rrggbb` en trois octets. Toute autre écriture est refusée à l'entrée. */
function rgb(color: string): [number, number, number] {
  const match = /^#([0-9a-f]{6})$/i.exec(color);
  if (!match?.[1]) throw new Error(`couleur illisible : ${color}`);
  const value = Number.parseInt(match[1], 16);
  return [(value >> 16) & 0xff, (value >> 8) & 0xff, value & 0xff];
}

/**
 * Taille de table de couleurs : une puissance de deux, au moins deux entrées.
 * Le format n'en accepte pas d'autre, et la case décrit l'exposant moins un.
 */
function tableSize(count: number): number {
  let size = 2;
  while (size < count) size *= 2;
  return Math.min(256, size);
}

/**
 * Compression LZW telle que le GIF l'attend — c'est-à-dire pas tout à fait la
 * LZW de Welch.
 *
 * Le point qui fait échouer la plupart des tentatives est le passage à un code
 * plus large. L'encodeur ajoute son entrée de dictionnaire **en même temps**
 * qu'il émet un code, tandis que le décodeur ne peut ajouter la sienne qu'au
 * code suivant : à tout instant l'encodeur a une entrée d'avance. Il doit donc
 * s'élargir un cran plus tard qu'il n'y paraît — d'où le `+ 1` ci-dessous, sans
 * lequel le fichier se décode en bouillie à partir du 32e motif.
 */
function compress(indices: Uint8Array, minCodeSize: number): number[] {
  const clearCode = 1 << minCodeSize;
  const endCode = clearCode + 1;
  const out: number[] = [];

  let codeSize = minCodeSize + 1;
  let nextCode = endCode + 1;
  let dictionary = new Map<number, number>();

  let buffer = 0;
  let bits = 0;
  const emit = (code: number): void => {
    buffer |= code << bits;
    bits += codeSize;
    while (bits >= 8) {
      out.push(buffer & 0xff);
      buffer >>= 8;
      bits -= 8;
    }
  };

  emit(clearCode);

  if (indices.length > 0) {
    let prefix = indices[0] ?? 0;
    for (let index = 1; index < indices.length; index += 1) {
      const next = indices[index] ?? 0;
      // Clé compacte : le préfixe tient sur douze bits, le pixel sur huit.
      const key = (prefix << 8) | next;
      const known = dictionary.get(key);
      if (known !== undefined) {
        prefix = known;
        continue;
      }

      emit(prefix);
      dictionary.set(key, nextCode);
      nextCode += 1;

      if (nextCode === 4096) {
        // Le dictionnaire est plein : on repart de zéro, des deux côtés.
        emit(clearCode);
        dictionary = new Map();
        nextCode = endCode + 1;
        codeSize = minCodeSize + 1;
      } else if (codeSize < 12 && nextCode === (1 << codeSize) + 1) {
        codeSize += 1;
      }
      prefix = next;
    }
    emit(prefix);
  }

  emit(endCode);
  if (bits > 0) out.push(buffer & 0xff);
  return out;
}

/** Les données compressées, découpées en sous-blocs de 255 octets au plus. */
function subBlocks(data: readonly number[], bytes: Bytes): void {
  for (let start = 0; start < data.length; start += 255) {
    const chunk = data.slice(start, start + 255);
    bytes.byte(chunk.length);
    bytes.all(chunk);
  }
  bytes.byte(0);
}

/**
 * Fabrique le fichier.
 *
 * Une seule palette pour toute l'animation : les bannières de 88 × 31 n'en ont
 * jamais eu besoin de deux, et une table locale par image aurait pesé plus
 * lourd que les images elles-mêmes.
 */
export function encodeGif(spec: GifSpec): Uint8Array<ArrayBuffer> {
  const { width, height, palette, frames } = spec;
  if (width < 1 || height < 1) throw new Error('dimensions vides');
  if (palette.length < 1 || palette.length > 256) throw new Error('palette hors bornes');
  if (frames.length < 1) throw new Error('aucune image');

  const size = tableSize(palette.length);
  const exponent = Math.log2(size) - 1;
  const bytes = new Bytes();

  bytes.ascii('GIF89a');
  bytes.short(width);
  bytes.short(height);
  // Table globale présente, profondeur maximale, table non triée, sa taille.
  bytes.byte(0b1111_0000 | exponent);
  bytes.byte(0); // couleur de fond : la première de la table
  bytes.byte(0); // rapport de pixel : carré

  for (let index = 0; index < size; index += 1) {
    const color = palette[index] ?? palette[0] ?? '#000000';
    bytes.all(rgb(color));
  }

  /*
   * Le bloc « NETSCAPE2.0 » est ce qui fait boucler un GIF. Il n'a jamais été
   * dans la norme : c'est une extension d'application que Netscape 2 lisait, et
   * que tout le monde lit depuis. Sans lui, la bannière clignote une fois puis
   * s'arrête.
   */
  bytes.byte(0x21);
  bytes.byte(0xff);
  bytes.byte(11);
  bytes.ascii('NETSCAPE2.0');
  bytes.byte(3);
  bytes.byte(1);
  bytes.short(0); // zéro tour = sans fin
  bytes.byte(0);

  const minCodeSize = Math.max(2, Math.ceil(Math.log2(size)));

  for (const frame of frames) {
    if (frame.indices.length !== width * height) throw new Error('image mal dimensionnée');

    bytes.byte(0x21);
    bytes.byte(0xf9);
    bytes.byte(4);
    bytes.byte(0b0000_0100); // méthode de nettoyage 1 : on laisse en place
    bytes.short(Math.max(0, Math.round(frame.delayMs / 10)));
    bytes.byte(0); // aucune couleur transparente
    bytes.byte(0);

    bytes.byte(0x2c);
    bytes.short(0);
    bytes.short(0);
    bytes.short(width);
    bytes.short(height);
    bytes.byte(0); // pas de table locale, pas d'entrelacement

    bytes.byte(minCodeSize);
    subBlocks(compress(frame.indices, minCodeSize), bytes);
  }

  bytes.byte(0x3b);
  return bytes.done();
}
